/*
  UserManagement.jsx
  ----------------
  Interface de gestion des utilisateurs.
*/
import { useEffect, useState } from "react";
import { Tabs, Card, Loader, Table, Button, TextInput, Select, Modal, MultiSelect, Group, Badge } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useUser } from "../tools/Context";
import { get, post } from "../tools/Request";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({
    email: "",
    firstname: "",
    lastname: "",
    type: "",
    role: "",
    isratp: false,
    supplier_number: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [authorizedMarkets, setAuthorizedMarkets] = useState([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const { token, role } = useUser();
  const isAdmin = role === "administrateur";

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersResponse, suppliersResponse] = await Promise.all([
        get("users/getAllUser?includeInactive=true", null, token),
        get("supplier/getAllSuppliers", null, token),
      ]);

      const usersWithSupplierNames = usersResponse.map((user) => {
        const supplier = suppliersResponse.find((supplier) => supplier.supplier_number === user.supplier_number);
        return {
          ...user,
          supplier_name: supplier ? supplier.supplier_name : "",
        };
      });

      setUsers(usersWithSupplierNames);
      setSuppliers(suppliersResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification({
        title: "Erreur",
        message: "Échec du chargement des données.",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleToggleActive = async () => {
    if (!userToToggle) return;
    const desiredActif = !userToToggle.actif;

    try {
      await post("users/setActive", { userId: userToToggle.id, actif: desiredActif }, token);
      await fetchData();
      showNotification({
        title: "Succès",
        message: desiredActif ? "Utilisateur réactivé avec succès !" : "Utilisateur désactivé avec succès !",
        color: "green",
      });
      setConfirmationModalOpen(false);
    } catch (error) {
      console.error("Error toggling user active state:", error);
      showNotification({
        title: "Erreur",
        message: "Échec de la mise à jour du statut de l'utilisateur.",
        color: "red",
      });
    }
  };

  const handleCreateUser = async () => {
    try {
      await post(
        "users/createUser",
        {
          ...newUser,
          isratp: newUser.type === "ratp",
          supplier_number: newUser.type === "fournisseur" ? newUser.supplier_number : null,
        },
        token
      );

      await fetchData();
      setNewUser({
        email: "",
        firstname: "",
        lastname: "",
        type: "",
        role: "",
        isratp: false,
        supplier_number: "",
      });
      setModalOpen(false);
      showNotification({
        title: "Succès",
        message: "Utilisateur créé avec succès !",
        color: "green",
      });
    } catch (error) {
      console.error("Error creating user:", error);
      showNotification({
        title: "Erreur",
        message: "Échec de la création de l'utilisateur.",
        color: "red",
      });
    }
  };

  const fetchAuthorizedMarkets = async (supplierNumber = null) => {
    try {
      const url = supplierNumber
        ? `users/getAuthorizedMarkets?supplierNumber=${supplierNumber}`
        : "users/getAuthorizedMarkets";

      const response = await get(url, null, token);
      setAuthorizedMarkets(response.map((market) => market.market_number));
    } catch (error) {
      console.error("Error fetching authorized markets:", error);
      showNotification({
        title: "Erreur",
        message: "Échec de la récupération des marchés autorisés.",
        color: "red",
      });
    }
  };

  const handleEditMarkets = async (userId, markets) => {
    try {
      await post(`users/updateAuthorizedMarkets/${userId}`, { authorized_markets: markets }, token);
      await fetchData();
      setEditModalOpen(false);
      showNotification({
        title: "Succès",
        message: "Marchés autorisés mis à jour avec succès !",
        color: "green",
      });
    } catch (error) {
      console.error("Error updating authorized markets:", error);
      showNotification({
        title: "Erreur",
        message: "Échec de la mise à jour des marchés autorisés.",
        color: "red",
      });
    }
  };

  const handleEditUser = async () => {
    try {
      await post(
        `users/updateUser/${selectedUser.id}`,
        {
          email: selectedUser.email,
          firstname: selectedUser.firstname,
          lastname: selectedUser.lastname,
          role: selectedUser.role,
          isratp: selectedUser.isratp,
          supplier_number: selectedUser.supplier_number,
        },
        token
      );

      await fetchData();
      setEditUserModalOpen(false);
      showNotification({
        title: "Succès",
        message: "Utilisateur mis à jour avec succès !",
        color: "green",
      });
    } catch (error) {
      console.error("Error updating user:", error);
      showNotification({
        title: "Erreur",
        message: "Échec de la mise à jour de l'utilisateur.",
        color: "red",
      });
    }
  };

  useEffect(() => {
    if (selectedUser) {
      if (selectedUser.isratp) {
        fetchAuthorizedMarkets();
      } else {
        fetchAuthorizedMarkets(selectedUser.supplier_number);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const filteredUsers = users;
  const ratpUsers = filteredUsers.filter((user) => user.isratp);
  const fournisseurUsers = filteredUsers.filter((user) => !user.isratp);

  return (
    <>
      {isAdmin && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Button
            size="xs"
            w="20%"
            variant="light"
            color="#0a0082"
            onClick={() => setModalOpen(true)}
            style={{ marginBottom: "1rem" }}
          >
            Créer un utilisateur
          </Button>

          <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title="Créer un nouvel utilisateur">
            <TextInput
              label="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <TextInput
              label="Prénom"
              value={newUser.firstname}
              onChange={(e) => setNewUser({ ...newUser, firstname: e.target.value })}
            />
            <TextInput
              label="Nom"
              value={newUser.lastname}
              onChange={(e) => setNewUser({ ...newUser, lastname: e.target.value })}
            />
            <Select
              label="Type"
              value={newUser.type}
              onChange={(value) => {
                setNewUser({ ...newUser, type: value, role: "" });
              }}
              data={[
                { value: "ratp", label: "RATP" },
                { value: "fournisseur", label: "Fournisseur" },
              ]}
            />
            {newUser.type === "ratp" && (
              <Select
                label="Rôle"
                value={newUser.role}
                onChange={(value) => setNewUser({ ...newUser, role: value })}
                data={[
                  { value: "chef de projet", label: "Chef de Projet" },
                  { value: "maitre d'oeuvre", label: "Maître d'Oeuvre" },
                  { value: "administrateur", label: "Administrateur" },
                ]}
              />
            )}
            {newUser.type === "fournisseur" && (
              <>
                <Select
                  label="Rôle"
                  value={newUser.role}
                  onChange={(value) => setNewUser({ ...newUser, role: value })}
                  data={[{ value: "titulaire de marché", label: "Titulaire de Marché" }]}
                />

                <Select
                  label="Entreprise"
                  searchable
                  value={newUser.supplier_number}
                  onChange={(value) => setNewUser({ ...newUser, supplier_number: value })}
                  data={suppliers.map((supplier) => ({
                    value: supplier.supplier_number,
                    label: supplier.supplier_name,
                  }))}
                />
              </>
            )}

            <Button color="#0a0082" onClick={handleCreateUser} style={{ marginTop: "1rem" }}>
              Créer l'utilisateur
            </Button>
          </Modal>

          <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title="Modifier les marchés autorisés">
            <MultiSelect
              label="Marchés Autorisés"
              value={selectedUser?.authorized_markets || []}
              onChange={(value) =>
                setSelectedUser((prevUser) => ({
                  ...prevUser,
                  authorized_markets: value,
                }))
              }
              searchable
              data={authorizedMarkets.map((market) => ({
                value: market,
                label: market,
              }))}
            />
            <Button
              onClick={() => {
                handleEditMarkets(selectedUser.id, selectedUser.authorized_markets);
                setEditUserModalOpen(true);
              }}
              style={{ marginTop: "1rem" }}
            >
              Enregistrer
            </Button>
          </Modal>

          <Modal opened={editUserModalOpen} onClose={() => setEditUserModalOpen(false)} title="Modifier l'utilisateur">
            <TextInput
              label="Email"
              value={selectedUser?.email || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
            />
            <TextInput
              label="Prénom"
              value={selectedUser?.firstname || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, firstname: e.target.value })}
            />
            <TextInput
              label="Nom"
              value={selectedUser?.lastname || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, lastname: e.target.value })}
            />
            <Select
              label="Rôle"
              value={selectedUser?.role || ""}
              onChange={(value) => setSelectedUser({ ...selectedUser, role: value })}
              data={[
                { value: "chef de projet", label: "Chef de Projet" },
                { value: "maitre d'oeuvre", label: "Maître d'Oeuvre" },
                { value: "titulaire de marché", label: "Titulaire de Marché" },
                { value: "administrateur", label: "Administrateur" },
              ]}
            />
            <Select
              label="Type"
              value={selectedUser?.isratp ? "ratp" : "fournisseur"}
              onChange={(value) => setSelectedUser({ ...selectedUser, isratp: value === "ratp" })}
              data={[
                { value: "ratp", label: "RATP" },
                { value: "fournisseur", label: "Fournisseur" },
              ]}
            />
            {selectedUser?.isratp === false && (
              <Select
                label="Entreprise"
                searchable
                value={selectedUser?.supplier_number || ""}
                onChange={(value) => setSelectedUser({ ...selectedUser, supplier_number: value })}
                data={suppliers.map((supplier) => ({
                  value: supplier.supplier_number,
                  label: supplier.supplier_name,
                }))}
              />
            )}
            <Group justify="space-between">
              {selectedUser?.role !== "administrateur" && (
                <Button
                  variant="light"
                  color="#0a0082"
                  onClick={() => {
                    setEditModalOpen(true);
                    setEditUserModalOpen(false);
                  }}
                  style={{ marginTop: "1rem" }}
                >
                  Modifier les Marchés Autorisés
                </Button>
              )}
              <Button color="#0a0082" onClick={handleEditUser} style={{ marginTop: "1rem" }}>
                Enregistrer
              </Button>
            </Group>
          </Modal>

          {loading ? (
            <Loader />
          ) : (
            <Tabs defaultValue="ratp">
              <Tabs.List>
                <Tabs.Tab value="ratp">Utilisateurs RATP</Tabs.Tab>
                <Tabs.Tab value="fournisseur">Fournisseurs</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="ratp" pt="xs">
                {ratpUsers.length > 0 ? (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Nom</Table.Th>
                        <Table.Th>Prénom</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Rôle</Table.Th>
                        <Table.Th>Marchés Autorisés</Table.Th>
                        <Table.Th>Statut</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {ratpUsers.map((user) => (
                        <Table.Tr key={user.id}>
                          <Table.Td>{user.lastname}</Table.Td>
                          <Table.Td>{user.firstname}</Table.Td>
                          <Table.Td>{user.email}</Table.Td>
                          <Table.Td>
                            {user.role === "maitre d'oeuvre"
                              ? "MOE"
                              : user.role === "chef de projet"
                              ? "CdP"
                              : user.role}
                          </Table.Td>
                          <Table.Td>{user.authorized_markets.join(", ")}</Table.Td>
                          <Table.Td>
                            <Badge variant="light" color={user.actif ? "green" : "gray"}>
                              {user.actif ? "Actif" : "Désactivé"}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              variant="light"
                              color={user.actif ? "#e2231a" : "green"}
                              size="xs"
                              onClick={() => {
                                setUserToToggle(user);
                                setConfirmationModalOpen(true);
                              }}
                            >
                              {user.actif ? "Désactiver" : "Réactiver"}
                            </Button>
                            <Button
                              variant="light"
                              color="#0a0082"
                              size="xs"
                              onClick={() => {
                                setSelectedUser(user);
                                setEditUserModalOpen(true);
                              }}
                              disabled={user.actif ? false : true}
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Modifier
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <p>Aucun utilisateur RATP trouvé.</p>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="fournisseur" pt="xs">
                {fournisseurUsers.length > 0 ? (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Nom</Table.Th>
                        <Table.Th>Prénom</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Rôle</Table.Th>
                        <Table.Th>Entreprise</Table.Th>
                        <Table.Th>Marchés Autorisés</Table.Th>
                        <Table.Th>Statut</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {fournisseurUsers.map((user) => (
                        <Table.Tr key={user.id}>
                          <Table.Td>{user.lastname}</Table.Td>
                          <Table.Td>{user.firstname}</Table.Td>
                          <Table.Td>{user.email}</Table.Td>
                          <Table.Td>{user.role}</Table.Td>
                          <Table.Td>{user.supplier_name}</Table.Td>
                          <Table.Td>{user.authorized_markets.join(", ")}</Table.Td>
                          <Table.Td>
                            <Badge variant="light" color={user.actif ? "green" : "gray"}>
                              {user.actif ? "Actif" : "Désactivé"}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              variant="light"
                              color={user.actif ? "#e2231a" : "green"}
                              size="xs"
                              onClick={() => {
                                setUserToToggle(user);
                                setConfirmationModalOpen(true);
                              }}
                            >
                              {user.actif ? "Désactiver" : "Réactivé"}
                            </Button>
                            <Button
                              variant="light"
                              color="#0a0082"
                              size="xs"
                              onClick={() => {
                                setSelectedUser(user);
                                setEditUserModalOpen(true);
                              }}
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Modifier
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <p>Aucun fournisseur trouvé.</p>
                )}
              </Tabs.Panel>
            </Tabs>
          )}

          <Modal
            opened={confirmationModalOpen}
            onClose={() => setConfirmationModalOpen(false)}
            title={userToToggle?.actif ? "Confirmer la désactivation" : "Confirmer la réactivation"}
          >
            <p>Êtes-vous sûr de vouloir {userToToggle?.actif ? "désactiver" : "réactiver"} cet utilisateur ?</p>
            <Button
              color={userToToggle?.actif ? "red" : "green"}
              onClick={handleToggleActive}
              style={{ marginTop: "1rem" }}
            >
              {userToToggle?.actif ? "Désactiver" : "Réactiver"}
            </Button>
            <Button
              variant="light"
              onClick={() => setConfirmationModalOpen(false)}
              style={{ marginTop: "1rem", marginLeft: "1rem" }}
            >
              Annuler
            </Button>
          </Modal>
        </Card>
      )}
    </>
  );
}
